import React, { useState } from 'react';
import { Button } from '../components/Button';
import { TestimonialSection } from '../components/TestimonialSection';
import { PricingSection } from '../components/PricingSection';
import { TestimonialCarousel } from '../components/TestimonialCarousel';
import { PartnerSection } from '../components/PartnerSection';
import { TechStackSection } from '../components/TechStackSection';
import { WhyUsSection } from '../components/WhyUsSection';
import { ProcessSection } from '../components/ProcessSection';
import { IndustriesStrip } from '../components/IndustriesStrip';
import { TrustedBySection } from '../components/TrustedBySection';

export default function Home() {
  const [isHovered, setIsHovered] = useState(false);
  const [magneticPos, setMagneticPos] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    setMagneticPos({ x: x * 0.12, y: y * 0.12 });
  };

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => {
    setIsHovered(false);
    setMagneticPos({ x: 0, y: 0 });
  };

  return (
    <div data-testid="home-page" className="relative pb-24 overflow-x-hidden">
      {/* HERO */}
      <section className="relative mx-auto max-w-[720px] px-6 pt-14 md:pt-20 flex flex-col items-center text-center">
        {/* Techy backdrop */}
        <div className="absolute inset-0 tech-grid opacity-40 pointer-events-none [mask-image:radial-gradient(circle_at_center,black_30%,transparent_75%)]" />
        <div className="absolute inset-0 violet-glow pointer-events-none animate-pulse-glow" />
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-[#7C3AED]/12 blur-3xl pointer-events-none" />
        <div className="absolute top-40 -left-16 w-64 h-64 rounded-full bg-[#22D3EE]/10 blur-3xl pointer-events-none" />
        <div className="absolute top-40 -right-16 w-64 h-64 rounded-full bg-[#EC4899]/10 blur-3xl pointer-events-none" />

        {/* Eyebrow badge */}
        <div
          className="relative inline-flex items-center gap-2 rounded-full border border-[#7C3AED]/25 bg-white/70 backdrop-blur px-4 py-1.5 shadow-[0_4px_20px_rgba(124,58,237,0.12)] animate-fade-in-up"
          style={{ animationDelay: '0.05s' }}
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#7C3AED] opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#7C3AED]" />
          </span>
          <span className="font-mono text-[10px] md:text-[11px] tracking-[0.22em] uppercase text-[#4C1D95]">
            Enterprise AI &amp; Software Engineering
          </span>
        </div>

        <h1
          data-testid="hero-title"
          onMouseMove={handleMouseMove}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className="font-display relative mt-6 text-[40px] md:text-[58px] lg:text-[68px] leading-[1.02] text-[#0D212C] tracking-[-0.028em] animate-fade-in-up transition-all duration-300 ease-out"
          style={{
            animationDelay: '0.15s',
            transform: isHovered
              ? `translate(${magneticPos.x}px, ${magneticPos.y}px)`
              : 'translate(0px, 0px)'
          }}
        >
          Building <span className="font-serif grad-text">Intelligent Software</span><br />
          for <span className="font-serif grad-text">Modern Enterprises.</span>
        </h1>

        <p
          className="font-serif text-2xl md:text-3xl text-[#4C1D95] mt-6 animate-fade-in-up relative"
          style={{ animationDelay: '0.25s' }}
        >
          Your Imagination. Our Creation.
        </p>

        <p
          className="text-sm md:text-base text-[#273C46] leading-relaxed mt-6 max-w-xl animate-fade-in-up relative"
          style={{ animationDelay: '0.35s' }}
        >
          From AI-powered applications to enterprise software, YP Nova transforms
          ambitious ideas into scalable digital products that drive measurable
          business growth.
        </p>

        <div
          className="flex flex-col sm:flex-row justify-center gap-3 md:gap-4 mt-8 animate-fade-in-up relative"
          style={{ animationDelay: '0.45s' }}
        >
          <Button to="/contact" variant="primary" data-testid="hero-cta-start">
            Book a Discovery Call
          </Button>
          <Button to="/case-studies" variant="secondary" data-testid="hero-cta-work">
            View Case Studies
          </Button>
        </div>

        {/* Terminal chip flourish */}
        <div
          className="mt-10 inline-flex items-center gap-2 rounded-full border border-[#0D212C]/10 bg-[#0B0F1A] text-white/90 pl-3 pr-4 py-1.5 shadow-[0_10px_30px_rgba(11,15,26,0.25)] animate-fade-in-up relative"
          style={{ animationDelay: '0.55s' }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          <span className="font-mono text-[11px] text-white/80">$ ypnova --build enterprise-grade</span>
        </div>
      </section>

      {/* Animated dashboard testimonial */}
      <TestimonialSection />

      {/* Pricing (centred pair) */}
      <PricingSection />

      {/* Enterprise leaders carousel */}
      <TestimonialCarousel />

      {/* Technology Stack */}
      <TechStackSection />

      {/* Why YP Nova */}
      <WhyUsSection />

      {/* Development Process */}
      <ProcessSection />

      {/* Industries strip */}
      <IndustriesStrip />

      {/* Trusted by client logos */}
      <TrustedBySection />

      {/* Bottom CTA */}
      <PartnerSection />
    </div>
  );
}
